import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useMemo } from 'react';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { ExpandMore as ExpandMoreIcon, Download as DownloadIcon, CheckCircle as SuccessIcon, Error as ErrorIcon, Close as CloseIcon } from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Checkbox, FormGroup, CircularProgress, LinearProgress, MenuItem, TextField, Box, Typography, Chip, Alert, IconButton } from '@mui/material';

import './ExportWizard.css';
import constants from '../constants';
import { initiateExport, executeExport, clearExportData } from '../store/Objects';

/**
 * Side-panel export drawer (HubSpot-style). Slides in from the right while keeping the list page
 * visible behind it. Inherits the list page's filters and record count, lets the user name the
 * export, pick format/columns, and toggle association columns.
**/
const ExportWizard = ({ objectType: initialObjectType, exportWizardOpen, setExportWizardOpen, selectedIds = [], activeFilters, filterMongoQuery, totalRecords, activeView }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const objectType = initialObjectType || 'contacts';
  const objectData = useSelector(state => state['Objects']?.[objectType]);
  const isLoading = objectData?.['loading'];
  const exportStatus = objectData?.['exportStatus'];
  const exportPreview = objectData?.['exportPreview'];
  const objectSchema = constants.getObjectSchema(objectType);

  // Grouped state: `form` for user inputs, `ui` for wizard flow / transient UI state.
  const [ui, setUi] = useState(INITIAL_UI);
  const [form, setForm] = useState(INITIAL_FORM);
  const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const updateUi = (patch) => setUi(prev => ({ ...prev, ...patch }));

  // Reset state and request a fresh preview every time the drawer opens.
  useEffect(() => {
    if (!exportWizardOpen) return;
    setUi(INITIAL_UI);
    dispatch(clearExportData({ objectType }));
    setForm({ ...INITIAL_FORM, name: defaultExportName(objectSchema) });

    dispatch(initiateExport({
      objectType,
      config: { selectedIds, filters: filterMongoQuery, format: 'csv', viewId: activeView?.['_id'] },
      successCallback: (data) => {
        const standardFields = data['availableFields']?.filter(f => f['type'] === 'standard').map(f => f['key']) || [];
        updateUi({ jobId: data['jobId'] });
        updateForm({ selectedFields: standardFields });
      },
      failCallback: (err) => updateUi({ errorMessage: err?.['response']?.['data']?.['message'] || 'Failed to initialize export.' })
    }));
  }, [exportWizardOpen, objectType]);

  const recordCount = useMemo(() => {
    if (selectedIds && selectedIds.length > 0) return selectedIds.length;
    if (exportPreview?.['totalRecords'] != null) return exportPreview['totalRecords'];
    return totalRecords || 0;
  }, [selectedIds, exportPreview, totalRecords]);

  const filterChips = useMemo(() => {
    const flat = (activeFilters?.['filterGroups'] || []).flatMap(g => g['filters'] || []);
    return flat.map(formatFilterChip).filter(Boolean);
  }, [activeFilters]);

  const fieldGroups = useMemo(() => {
    if (!exportPreview?.['availableFields']) return {};
    return exportPreview['availableFields'].reduce((acc, field) => {
      if (!acc[field['group']]) acc[field['group']] = [];
      acc[field['group']].push(field);
      return acc;
    }, {});
  }, [exportPreview]);

  const handleToggleField = (fieldKey) => {
    updateForm({ selectedFields: form['selectedFields'].includes(fieldKey) ? form['selectedFields'].filter(f => f !== fieldKey) : [...form['selectedFields'], fieldKey] });
  }

  const handleToggleGroup = (group) => {
    const groupFieldKeys = (fieldGroups[group] || []).map(f => f['key']);
    const hasAll = groupFieldKeys.every(k => form['selectedFields'].includes(k));
    updateForm({
      selectedFields: hasAll ? form['selectedFields'].filter(f => !groupFieldKeys.includes(f)) : [...new Set([...form['selectedFields'], ...groupFieldKeys])]
    });
  }

  const handleToggleAssociation = (type) => {
    const exists = form['includeAssociations'].find(a => a['type'] === type);
    updateForm({
      includeAssociations: exists
        ? form['includeAssociations'].filter(a => a['type'] !== type)
        : [...form['includeAssociations'], { type, includeName: true, maxPerRecord: 1000 }]
    })
  }

  const handleToggleAssociationName = (type) => {
    updateForm({ includeAssociations: form['includeAssociations'].map(a => a['type'] === type ? { ...a, includeName: !a['includeName'] } : a) })
  }

  const handleClose = () => {
    setExportWizardOpen(false);
    dispatch(clearExportData({ objectType }));
  };

  const handleSubmit = () => {
    if (!ui['jobId'] || form['selectedFields'].length === 0) return;
    updateUi({ phase: 'processing', processingStartedAt: Date.now() });
    dispatch(executeExport({
      objectType,
      jobId: ui['jobId'],
      config: { ...form, filters: filterMongoQuery },
      // Note: the POST returning success just means the job moved to 'processing' on the server.
      // Don't transition phase here — let the polled status (below) drive 'done'.
      failCallback: (err) => updateUi({ phase: 'done', errorMessage: err?.['response']?.['data']?.['message'] || 'Export failed.' })
    }));
  };

  const renderConfigure = () => (
    <ValidatorForm onSubmit={handleSubmit} className="export-drawer-form">
      <div className="export-drawer-context">
        <div className="export-drawer-count">
          <span className="export-drawer-count-label">Exporting:</span>
          <strong>{recordCount.toLocaleString()} {objectSchema?.['plural'] || objectType}</strong>
        </div>
        {filterChips.length > 0 ? (
          <div className="export-drawer-filter-chips">
            {filterChips.map((label, i) => <Chip key={i} label={label} size="small" className="export-drawer-chip" />)}
          </div>
        ) : (
          <p className="export-drawer-context-hint">All records — adjust filters on the list page to narrow.</p>
        )}
      </div>

      <div className="export-drawer-row">
        <TextValidator
          name="exportName"
          label="Export name"
          value={form['name']}
          onChange={(e) => updateForm({ name: e['target']['value'] })}
          validators={['required']}
          errorMessages={['Required']}
          size="small"
          fullWidth
        />
      </div>

      <div className="export-drawer-row export-drawer-row-split">
        <TextField select size="small" label="File format" value={form['format']} onChange={(e) => updateForm({ format: e['target']['value'] })} fullWidth>
          {Object.values(constants['exportFormats']).map(f => <MenuItem key={f['value']} value={f['value']}>{f['label']}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Date format" value={form['dateFormat']} onChange={(e) => updateForm({ dateFormat: e['target']['value'] })} fullWidth>
          {DATE_FORMATS.map(d => <MenuItem key={d['value']} value={d['value']}>{d['label']}</MenuItem>)}
        </TextField>
      </div>

      {form['format'] === 'csv' && (
        <FormControlLabel
          className="export-drawer-headers-toggle"
          control={<Checkbox checked={form['includeHeaders']} onChange={(e) => updateForm({ includeHeaders: e['target']['checked'] })} />}
          label="Include column headers"
        />
      )}

      <Accordion
        expanded={ui['isCustomizeExpanded']}
        onChange={() => updateUi({ isCustomizeExpanded: !ui['isCustomizeExpanded'] })}
        className="export-drawer-customize"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <span className="export-drawer-customize-title">Customize</span>
        </AccordionSummary>
        <AccordionDetails>
          <div className="export-drawer-customize-section">
            <h4 className="export-drawer-customize-heading">Properties included in export</h4>
            {isLoading || !exportPreview ? (
              <Box className="export-drawer-loader"><CircularProgress size={20} /></Box>
            ) : (
              <div className="export-drawer-property-groups">
                {Object.entries(fieldGroups).map(([group, fields]) => {
                  const groupFieldKeys = fields.map(f => f['key']);
                  const hasAll = groupFieldKeys.every(k => form['selectedFields'].includes(k));
                  const hasSome = groupFieldKeys.some(k => form['selectedFields'].includes(k));
                  return (
                    <div key={group} className="export-drawer-property-group">
                      <FormControlLabel
                        className="export-drawer-property-group-toggle"
                        control={<Checkbox size="small" checked={hasAll} indeterminate={hasSome && !hasAll} onChange={() => handleToggleGroup(group)} />}
                        label={<span className="export-drawer-property-group-label">{group}</span>}
                      />
                      <FormGroup className="export-drawer-property-list">
                        {fields.map(field => (
                          <FormControlLabel
                            key={field['key']}
                            className="export-drawer-property-item"
                            control={<Checkbox size="small" checked={form['selectedFields'].includes(field['key'])} onChange={() => handleToggleField(field['key'])} />}
                            label={field['label']}
                          />
                        ))}
                      </FormGroup>
                    </div>
                  );
                })}
                {form['selectedFields'].length === 0 && (
                  <Alert severity="warning" className="export-drawer-warning">Select at least one field to export.</Alert>
                )}
              </div>
            )}
          </div>

          {(exportPreview?.['availableAssociations'] || []).length > 0 && (
            <div className="export-drawer-customize-section">
              <h4 className="export-drawer-customize-heading">Associations included in export</h4>
              <p className="export-drawer-customize-hint">Each selected association adds an ID column (and optionally a Name column) for the related records.</p>
              {exportPreview['availableAssociations'].map(assoc => {
                const enabled = form['includeAssociations'].find(a => a['type'] === assoc['type']);
                return (
                  <div key={assoc['type']} className="export-drawer-assoc-row">
                    <FormControlLabel
                      className="export-drawer-assoc-toggle"
                      control={<Checkbox size="small" checked={!!enabled} onChange={() => handleToggleAssociation(assoc['type'])} />}
                      label={assoc['label']}
                    />
                    {enabled && (
                      <FormControlLabel
                        className="export-drawer-assoc-suboption"
                        control={<Checkbox size="small" checked={enabled['includeName']} onChange={() => handleToggleAssociationName(assoc['type'])} />}
                        label="Include record name"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      {ui['errorMessage'] && <Alert severity="error" className="export-drawer-error">{ui['errorMessage']}</Alert>}
    </ValidatorForm>
  );

  const renderProcessing = () => (
    <div className="export-drawer-progress">
      <CircularProgress />
      <Typography variant="h6">Exporting {objectSchema?.['plural'] || objectType}…</Typography>
      {exportStatus && (
        <>
          <LinearProgress variant="determinate" value={exportStatus['progress']?.['percentComplete'] || 0} className="export-drawer-progress-bar" />
          <Typography variant="body2" color="text.secondary">
            {exportStatus['progress']?.['processedRecords'] || 0} / {exportStatus['progress']?.['totalRecords'] || 0} records
          </Typography>
        </>
      )}
    </div>
  );

  const renderDone = () => {
    const isSuccess = exportStatus?.['status'] === 'completed';
    return (
      <div className="export-drawer-result">
        {isSuccess ? (
          <>
            <SuccessIcon className="export-drawer-result-icon export-drawer-result-success" />
            <Typography variant="h6">Export complete</Typography>
            <Typography variant="body2" color="text.secondary">
              {exportStatus['results']?.['recordsExported']} records · {formatFileSize(exportStatus['results']?.['fileSize'])}
            </Typography>
            <div className="export-drawer-result-actions">
              <button className="btn-style-new export-drawer-result-action" onClick={() => window.open(exportStatus['results']?.['downloadUrl'], '_blank')}>
                <DownloadIcon fontSize="small" /> Download {form['format'].toUpperCase()}
              </button>
              <button className="btn-style-border export-drawer-result-action" onClick={() => { handleClose(); history.push('/settings?tab=import-export'); }}>
                View export history
              </button>
            </div>
          </>
        ) : (
          <>
            <ErrorIcon className="export-drawer-result-icon export-drawer-result-error" />
            <Typography variant="h6" color="error">Export failed</Typography>
            <Typography variant="body2" color="text.secondary">{exportStatus?.['error']?.['message'] || ui['errorMessage'] || 'An error occurred.'}</Typography>
          </>
        )}
      </div>
    );
  };

  // Close on Escape — replicates the close-on-escape behaviour we'd get for free with MUI's Modal.
  useEffect(() => {
    if (!exportWizardOpen) return;
    const onKey = (e) => { if (e['key'] === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exportWizardOpen]);

  // The backend processes the export in the background; the polled status (set by the saga)
  // tells us when it's actually finished. Transition to 'done' only on a terminal status.
  // Enforce a minimum display time for the processing panel so small exports don't flash by.
  useEffect(() => {
    if (ui['phase'] !== 'processing') return;
    const status = exportStatus?.['status'];
    if (status !== 'completed' && status !== 'failed') return;
    const elapsed = Date.now() - (ui['processingStartedAt'] || 0);
    const remaining = Math.max(0, MIN_PROCESSING_DISPLAY_MS - elapsed);
    const timer = setTimeout(() => updateUi({ phase: 'done' }), remaining);
    return () => clearTimeout(timer);
  }, [exportStatus, ui['phase']]);

  if (!exportWizardOpen) return null;

  return (
    <aside className="export-drawer" role="dialog" aria-label={`Export ${objectSchema?.['plural'] || objectType}`}>
      <div className="export-drawer-content">
        <div className="export-drawer-header">
          <h3 className="export-drawer-title">Export {objectSchema?.['plural'] || objectType}</h3>
          <IconButton onClick={handleClose} size="small" aria-label="Close"><CloseIcon /></IconButton>
        </div>

        <div className="export-drawer-body">
          {ui['phase'] === 'configure' && renderConfigure()}
          {ui['phase'] === 'processing' && renderProcessing()}
          {ui['phase'] === 'done' && renderDone()}
        </div>

        {ui['phase'] === 'configure' && (
          <div className="export-drawer-footer">
            <button className="btn-style-border export-drawer-cancel" onClick={handleClose}>Cancel</button>
            <button
              className="btn-style-new export-drawer-submit"
              onClick={handleSubmit}
              disabled={!ui['jobId'] || form['selectedFields'].length === 0 || isLoading}
            >
              Start Export
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

// ==================== Constants & local helpers ====================

const DATE_FORMATS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-01-15)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/15/2024)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/01/2024)' },
  { value: 'MMMM D, YYYY', label: 'MMMM D, YYYY (January 15, 2024)' }
];

const INITIAL_FORM = {
  name: '',
  format: 'csv',
  includeHeaders: true,
  dateFormat: 'YYYY-MM-DD',
  selectedFields: [],
  includeAssociations: []
};

const INITIAL_UI = {
  phase: 'configure',        // 'configure' | 'processing' | 'done'
  jobId: null,
  isCustomizeExpanded: false,
  errorMessage: null,
  processingStartedAt: null
};

// Minimum time the processing panel stays visible — short exports finish in under 100ms
// and would otherwise flicker straight to the result panel.
const MIN_PROCESSING_DISPLAY_MS = 1200;

/** Convert a byte count into a short human-readable string (e.g. "1.45 MB"). */
function formatFileSize(bytes) {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/** Render a list-page filter entry as a short chip label. Mirrors the table's chip logic so the
 *  text reads identically (handles strings, arrays, `{label}`-style objects, and the `{ a: true,
 *  b: true }` map shape used by "any of" multi-select filters). */
function formatFilterChip(filter) {
  const property = filter['property']?.['label'] || filter['property']?.['name'] || '';
  const operator = filter['operator']?.['label'] || filter['operator']?.['value'] || '';
  let value = filter['value'];
  if (Array.isArray(value)) {
    value = value.map(v => (typeof v === 'object' && v !== null) ? (v['label'] || v['name'] || v['value'] || '') : String(v ?? '')).filter(Boolean).join(', ');
  } else if (typeof value === 'object' && value !== null) {
    const trueKeys = Object.keys(value).filter(k => value[k]);
    value = value['label'] || value['name'] || (trueKeys.length > 0 ? trueKeys.join(', ') : '');
  } else {
    value = value === null || value === undefined ? '' : String(value);
  }
  return `${property} ${operator} ${value}`.trim();
}

/** Default export name shown when the drawer opens. */
function defaultExportName(objectSchema) {
  const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const label = objectSchema?.['plural'] || 'Records';
  return `${label} export ${stamp}`;
}

export default ExportWizard;
